// src/components/CustomerCreateForm.jsx
import React, { useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import DatePicker from "react-multi-date-picker";
import { UserPlus } from "lucide-react";
import toast from "react-hot-toast";
import { getFormErrors } from "../utils/formErrors";
import { toastApiError, toastErrors } from "../utils/toastErrors";
import { CUSTOMER_LABELS, validateCustomer } from "../utils/recordValidation";

const CustomerCreateForm = () => {
  const [formData, setFormData] = useState({
    full_name: "",
    phone_number: "",
    national_code: "",
    customer_birthday: null, // stored as "YYYY-MM-DD" string or null
    customer_address: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const normalizeError = (value) => {
    if (!value) return "";
    if (Array.isArray(value)) return value.join(" ");
    if (typeof value === "string") return value;
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleDateChange = (date) => {
    // `date` is a DateObject (react-multi-date-picker)
    const value = date ? date.format("YYYY-MM-DD") : null;
    setFormData((prev) => ({
      ...prev,
      customer_birthday: value,
    }));
    setErrors((prev) => ({ ...prev, customer_birthday: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { lines: problems, fields } = validateCustomer(formData);
    if (problems.length) {
      setErrors(fields);
      toastErrors(problems);
      return;
    }
    setIsSubmitting(true);
    setErrors({});

    try {
      await axiosInstance.post("/accounts/customers/", formData);
      toast.success("مشتری با موفقیت ایجاد شد.");
      setFormData({
        full_name: "",
        phone_number: "",
        national_code: "",
        customer_birthday: null,
        customer_address: "",
      });
    } catch (error) {
      const { fields: fieldErrors, general } = getFormErrors(
        error,
        Object.keys(CUSTOMER_LABELS),
        "ایجاد مشتری ناموفق بود. لطفاً دوباره تلاش کنید.",
      );
      setErrors({ ...fieldErrors, general });
      toastApiError(error, CUSTOMER_LABELS, "ایجاد مشتری ناموفق بود. لطفاً دوباره تلاش کنید.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="mt-24 min-h-screen bg-slate-50 px-3 sm:px-4 md:px-6 pb-6 font-vazir"
      dir="rtl"
    >
      <div className="max-w-xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg border border-slate-200 px-5 sm:px-8 py-6 sm:py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3 flex-row-reverse">
              <div className="flex flex-col items-start">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-sky-500 to-indigo-500 flex items-center justify-center text-white">
                  <UserPlus className="w-5 h-5" />
                </div>
                <div className="flex flex-col items-start">
                  <h2 className="text-lg sm:text-xl font-bold text-slate-900">
                    ایجاد شخص جدید
                  </h2>
                  <p className="text-[0.75rem] text-slate-500">
                    ثبت اطلاعات مشتری برای استفاده در اسناد و عملیات
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Alert messages */}
          {errors.general && (
            <div className="mb-4 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 text-right">
              {normalizeError(errors.general)}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} noValidate className="space-y-4 sm:space-y-5">
            {/* Full Name */}
            <div>
              <label
                htmlFor="full_name"
                className="block text-sm font-medium text-slate-700 mb-1.5 text-right"
              >
                نام کامل
              </label>
              <input
                type="text"
                id="full_name"
                name="full_name"
                value={formData.full_name}
                onChange={handleChange}
                placeholder="نام کامل را وارد کنید"
                className={`w-full px-3 py-2.5 rounded-lg border text-sm text-right bg-white 
                  focus:outline-none focus:ring-2 
                  ${
                    errors.full_name
                      ? "border-rose-500 focus:ring-rose-500/70"
                      : "border-slate-300 focus:ring-sky-500/70"
                  }`}
              />
              {errors.full_name && (
                <span className="text-rose-600 text-xs mt-1 block text-right">
                  {normalizeError(errors.full_name)}
                </span>
              )}
            </div>

            {/* Phone / National code in a row on bigger screens */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Phone Number */}
              <div>
                <label
                  htmlFor="phone_number"
                  className="block text-sm font-medium text-slate-700 mb-1.5 text-right"
                >
                  شماره تلفن
                </label>
                <input
                  type="text"
                  id="phone_number"
                  name="phone_number"
                  value={formData.phone_number}
                  onChange={handleChange}
                  placeholder="مثلاً 09123456789"
                  className={`w-full px-3 py-2.5 rounded-lg border text-sm text-right bg-white 
                    focus:outline-none focus:ring-2 
                    ${
                      errors.phone_number
                        ? "border-rose-500 focus:ring-rose-500/70"
                        : "border-slate-300 focus:ring-sky-500/70"
                    }`}
                />
                {errors.phone_number && (
                  <span className="text-rose-600 text-xs mt-1 block text-right">
                    {normalizeError(errors.phone_number)}
                  </span>
                )}
              </div>

              {/* National Code */}
              <div>
                <label
                  htmlFor="national_code"
                  className="block text-sm font-medium text-slate-700 mb-1.5 text-right"
                >
                  کد ملی
                </label>
                <input
                  type="text"
                  id="national_code"
                  name="national_code"
                  value={formData.national_code}
                  onChange={handleChange}
                  placeholder="کد ملی را وارد کنید"
                  className={`w-full px-3 py-2.5 rounded-lg border text-sm text-right bg-white 
                    focus:outline-none focus:ring-2 
                    ${
                      errors.national_code
                        ? "border-rose-500 focus:ring-rose-500/70"
                        : "border-slate-300 focus:ring-sky-500/70"
                    }`}
                />
                {errors.national_code && (
                  <span className="text-rose-600 text-xs mt-1 block text-right">
                    {normalizeError(errors.national_code)}
                  </span>
                )}
              </div>
            </div>

            {/* Birthday */}
            <div>
              <label
                htmlFor="customer_birthday"
                className="block text-sm font-medium text-slate-700 mb-1.5 text-right"
              >
                تاریخ تولد
              </label>
              <DatePicker
                id="customer_birthday"
                name="customer_birthday"
                calendar={persian}
                locale={persian_fa}
                // react-multi-date-picker accepts string if format matches
                value={formData.customer_birthday || ""}
                onChange={handleDateChange}
                inputClass={`w-full px-3 py-2.5 rounded-lg border text-sm text-right font-vazir 
                  focus:outline-none focus:ring-2
                  ${
                    errors.customer_birthday
                      ? "border-rose-500 focus:ring-rose-500/70"
                      : "border-slate-300 focus:ring-sky-500/70"
                  }`}
                format="YYYY-MM-DD"
                placeholder="----/--/--"
              />
              {errors.customer_birthday && (
                <span className="text-rose-600 text-xs mt-1 block text-right">
                  {normalizeError(errors.customer_birthday)}
                </span>
              )}
            </div>

            {/* Address */}
            <div>
              <label
                htmlFor="customer_address"
                className="block text-sm font-medium text-slate-700 mb-1.5 text-right"
              >
                آدرس
              </label>
              <textarea
                id="customer_address"
                name="customer_address"
                rows={3}
                value={formData.customer_address}
                onChange={handleChange}
                placeholder="آدرس را وارد کنید"
                className={`w-full px-3 py-2.5 rounded-lg border text-sm text-right bg-white resize-y 
                  focus:outline-none focus:ring-2 
                  ${
                    errors.customer_address
                      ? "border-rose-500 focus:ring-rose-500/70"
                      : "border-slate-300 focus:ring-sky-500/70"
                  }`}
              />
              {errors.customer_address && (
                <span className="text-rose-600 text-xs mt-1 block text-right">
                  {normalizeError(errors.customer_address)}
                </span>
              )}
            </div>

            {/* Submit Button */}
            <div className="pt-2 flex justify-center">
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-1/2 inline-flex items-center justify-center gap-2 
                           px-4 py-2.5 rounded-xl text-sm font-semibold
                           bg-gradient-to-l from-sky-500 to-indigo-500 
                           text-white shadow-md hover:from-sky-600 hover:to-indigo-600
                           active:scale-[0.99] transition-all
                           disabled:bg-slate-400 disabled:cursor-not-allowed disabled:shadow-none"
              >
                {isSubmitting ? "در حال ایجاد..." : "ایجاد مشتری"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CustomerCreateForm;
